import React, { useState, useEffect, useRef } from 'react';
import { TextField, Grid } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import ErrorMessages from 'components/Global/ErrorMessages';
import mapValidators from 'utility/mapValidators';
import { checkValueLength } from 'utility/checkValidations';
import { useDispatch, useSelector } from 'react-redux';
import { SYSTEM_HANDLE_FORCE_CHECK_VALIDATION } from 'store/key';
import usePrevious from 'hooks/usePrevious';

const useStyles = makeStyles((theme) => ({
    autocomplete: {
        fontSize: 14,
        '& input': {
            fontSize: 14,
        },
        '& label': {
            fontSize: 14,
        },
    },
}));

export default function CustomAutocomplete(props) {
    const classes = useStyles();
    const {
        filterByGroup,
        value,
        freeSolo, // freeSolo === true means user input is not bound to provided options
        labelText,
        options,
        valueOnChange,
        name,
        error,
        helperText,
        isValidValue = () => null,
        forceErrorDisplay,
        validators,
        isRequired,
        minLength,
        maxLength,
        ...rest
    } = props;

    const forceCheckValidation = useSelector((state) => state.system.forceCheckValidation);
    const dispatch = useDispatch();

    const [errorMessage, setErrorMessage] = useState([]);
    const [isOnBlurTriggered, setOnBlurTrigger] = useState(false);

    const textField = useRef(value);

    const preValidators = usePrevious(validators);
    useEffect(() => {
        if (validators?.toString() !== preValidators?.toString()) {
            onValidation(value);
        };
        // eslint-disable-next-line
    }, [validators]);


    // setting textfield value when value changed, e.g. loading free-text value from user-profile
    useEffect(() => {
        if (value) {
            textField.current = value;
        } else {
            textField.current = '';
        }

        onValidation(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // For 'forceCheckValidation' case
    useEffect(() => {
        if (forceCheckValidation) {
            onValidation(value);
            dispatch({
                type: SYSTEM_HANDLE_FORCE_CHECK_VALIDATION,
                payload: {
                    forceCheckValidation: false,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceCheckValidation]);

    // logic to force error display
    useEffect(() => {
        if (forceErrorDisplay) {
            setOnBlurTrigger(true);
        }
    }, [forceErrorDisplay]);

    const onValidation = (value) => {
        let tempError = mapValidators(value, validators);
        tempError = [...tempError, ...checkValueLength(value, minLength, maxLength)];
        isValidValue(tempError.length === 0);
        setErrorMessage(tempError);
    };

    const handleValueChange = (e, newValue) => {
        let value = newValue ? newValue?.hospitalCode : '';
        if (freeSolo && !value) {
            value = textField.current;
        }

        valueOnChange({
            target: {
                value: value,
                name: name,
            },
        });
        setOnBlurTrigger(true);
    };

    const valueOnInputChange = (e, newValue) => {
        valueOnChange({
            target: {
                value: newValue,
                name: name,
            },
        });
        setOnBlurTrigger(true);
    };

    const getObjectValue = () => {
        const index = options.findIndex((item) => {
            return item.hospitalName === value || item.hospitalCode === value;
        });

        if (index === -1) {
            const val = textField.current ? { hospitalName: textField.current } : { hospitalName: '' };
            return freeSolo ? val : '';
        }

        return options[index];
    };

    const extra = {
        className: classes.autocomplete,
        style: { background: rest.disabled ? '#f7f7f7' : 'initial' },
        size: 'small',
        fullWidth: true,
        options: options,
        groupBy: (option) => (filterByGroup ? option.clusterName : null),
        getOptionLabel: (option) => {
            // Value inputed & not matched any option with enter, right from the input
            if (typeof option === 'string' && option !== '') {
                textField.current = option;
            }
            const text = textField.current ? textField.current : '';
            let label = option.hospitalName ? option.hospitalName : freeSolo ? text : '';
            return label;
        },
        onChange: (e, newValue) => handleValueChange(e, newValue),
        renderInput: (params) => (
            <TextField
                {...params}
                error={errorMessage.length > 0 && isOnBlurTriggered}
                onBlur={() => {
                    setOnBlurTrigger(true);
                }}
                label={labelText}
                variant="outlined"
            />
        ),
        value: getObjectValue(),
    };
    return (
        <Grid container>
            {freeSolo ? (
                <Autocomplete
                    freeSolo
                    onInputChange={(e, newValue) => {
                        if (!newValue) {
                            setOnBlurTrigger(true);
                            onValidation(newValue);
                            textField.current = newValue;
                            valueOnInputChange(e, newValue);
                        }
                    }}
                    onClose={(e, reason) => {
                        if (reason === 'blur') {
                            const newValue = e.target.value;
                            if (!newValue) {
                                setOnBlurTrigger(true);
                                onValidation(newValue);
                            }
    
                            textField.current = newValue;
                            valueOnInputChange(e, newValue);
                        }
                    }}
                    {...rest}
                    {...extra}
                />
            ) : (
                <Autocomplete {...rest} {...extra} />
            )}
            <ErrorMessages isOnBlurTriggered={isOnBlurTriggered} errorMessage={errorMessage} />
        </Grid>
    );
}
